import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Spinner, Button } from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';
import { MdFolderZip, MdDeselect } from "react-icons/md";
import DateHelper from "../../../primer/DateHelper/DateHelper";
import DateBox from "../../../primer/DateBox";
import Dropdown from "../../../primer/Dropdown";
import Pagination from "../../../primer/Pagination";
import { getCurrentUser } from "../../../../_helpers/commonFunctions";

const AIChatHistoryModal = ({ show, onHide, onSelectSession }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [usersOptions, setUsersOptions] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [exportingZip, setExportingZip] = useState(false);

    const [requestData, setRequestData] = useState({
        page: 1,
        date_from: moment().subtract(1, 'month').format("DD/MM/YYYY"),
        date_to: moment().format("DD/MM/YYYY"),
        users: ["current_user"]
    });

    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);

    useEffect(() => {
        if (show) {
            fetchAuthorizedUsers();
        }
    }, [show]);

    const fetchHistory = async (page = requestData.page, reqData = requestData) => {
        setLoading(true);
        setSelectedRows([]);
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL2}/ai/livechat/history`, {
                ...reqData,
                page: page,
                limit: 10
            });

            if (res.data.status === "200") {
                setHistory(res.data.data || []);
                setTotalPages(res.data.totalPages || 0);
                setTotalItems(res.data.totalRecords || 0);
            } else {
                toast.error("Σφάλμα κατά την ανάκτηση του ιστορικού.");
            }
        } catch (err) {
            console.error("Error fetching AI history:", err);
            toast.error("Αποτυχία σύνδεσης με τον διακομιστή.");
        } finally {
            setLoading(false);
        }
    };

    const fetchAuthorizedUsers = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL2}/user/authorized-chat-users`);
            if (res.data.status === "200") {
                const options = res.data.data.map(u => ({
                    label: `${u.firstName} ${u.lastName} (${u.userName})`,
                    value: u._id
                }));

                setUsersOptions(options);

                const currentUser = getCurrentUser();
                const currentUserId = currentUser?._id;

                setRequestData(prev => {
                    let newUsers = prev.users;
                    if (currentUserId && options.length > 0) {
                        const foundSelf = options.find(opt => String(opt.value) === String(currentUserId));
                        if (foundSelf) {
                            newUsers = [foundSelf.value];
                        }
                    }

                    const initialReqData = { ...prev, users: newUsers, page: 1 };
                    fetchHistory(1, initialReqData);

                    return initialReqData;
                });
            }
        } catch (error) {
            console.error("Error fetching authorized users:", error);
        }
    };

    const handleSelectRow = (e, id) => {
        e.stopPropagation();
        const idStr = String(id);

        if (selectedRows.includes("-1")) {
            if (selectedRows.includes(idStr)) {
                setSelectedRows(prev => prev.filter(i => i !== idStr));
            } else {
                setSelectedRows(prev => [...prev, idStr]);
            }
        } else {
            if (selectedRows.includes(idStr)) {
                setSelectedRows(prev => prev.filter(i => i !== idStr));
            } else {
                setSelectedRows(prev => [...prev, idStr]);
            }
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedRows(["-1"]);
        } else {
            setSelectedRows([]);
        }
    };

    const isAllChecked = (selectedRows.length === 1 && selectedRows[0] === "-1") ||
        (totalItems > 0 && selectedRows.length === totalItems && !selectedRows.includes("-1"));

    const handleExportZip = async () => {
        setExportingZip(true);
        try {
            const payloadRows = selectedRows.length === 0 ? ["-1"] : selectedRows;
            const response = await axios.post(`${process.env.REACT_APP_API_URL2}/ai/livechat/export-zip`, {
                ...requestData,
                selectedRows: payloadRows
            }, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `AI_Chat_Export_${moment().format("DD_MM_YYYY")}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Export Error:", err);
            toast.error("Σφάλμα κατά την εξαγωγή ZIP.");
        } finally {
            setExportingZip(false);
            setSelectedRows([]);
        }
    };

    const handleOnChange = (e, type, name) => {
        let updatedData = { ...requestData };
        if (type === "msdd") {
            updatedData[name] = e.flatMap((o) => o.value);
        } else if (type === "datebox") {
            if (moment(e).isValid()) {
                updatedData[name] = moment(e).format("DD/MM/YYYY");
            } else {
                updatedData[name] = "";
            }
        }
        setRequestData(updatedData);
    };

    const applyFilters = () => {
        const resetData = { ...requestData, page: 1 };
        setRequestData(resetData);
        fetchHistory(1, resetData);
    };

    const onPageChange = (newPage) => {
        const updatedData = { ...requestData, page: newPage };
        setRequestData(updatedData);
        setLoading(true);
        axios.post(`${process.env.REACT_APP_API_URL2}/ai/livechat/history`, {
            ...updatedData,
            page: newPage,
            limit: 10
        }).then(res => {
            if (res.data.status === "200") {
                setHistory(res.data.data || []);
                setTotalPages(res.data.totalPages || 0);
                setTotalItems(res.data.totalRecords || 0);
            }
        }).finally(() => {
            setLoading(false);
        });
    };

    let latestUpdateText = "";
    if (history && history.length > 0) {
        const latestMs = Math.max(...history.map(s => moment(s.updated_at).valueOf()));
        latestUpdateText = moment(latestMs).format("DD/MM/YYYY HH:mm:ss");
    }

    return (
        <Modal show={show} onHide={onHide} centered scrollable dialogClassName="modal65PercentWidth">
            <Modal.Header closeButton>
                <Modal.Title>Ιστορικό Συνομιλιών AI</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ minHeight: "500px" }}>
                <Row className="mb-3 align-items-end">
                    <Col sm={12} md={3}>
                        <DateHelper
                            dateFromAndToFunction={(from, to) => {
                                const newData = { ...requestData, date_from: from, date_to: to };
                                setRequestData(newData);
                            }}
                        />
                    </Col>
                    <Col sm={6} md={2}>
                        <DateBox
                            name={"date_from"}
                            label={"Ημερομηνία από"}
                            selected={moment(requestData.date_from, "DD/MM/YYYY").isValid() ? moment(requestData.date_from, "DD/MM/YYYY").toDate() : ""}
                            onChange={(e) => handleOnChange(e, "datebox", "date_from")}
                            allowOtherYears={true}
                        />
                    </Col>
                    <Col sm={6} md={2}>
                        <DateBox
                            name={"date_to"}
                            label={"Ημερομηνία μέχρι"}
                            selected={moment(requestData.date_to, "DD/MM/YYYY").isValid() ? moment(requestData.date_to, "DD/MM/YYYY").toDate() : ""}
                            onChange={(e) => handleOnChange(e, "datebox", "date_to")}
                            allowOtherYears={true}
                        />
                    </Col>
                    <Col sm={12} md={3}>
                        <Dropdown
                            className={"mt-1"}
                            name={"users"}
                            label={"Χρήστης"}
                            multiSelect={true}
                            options={usersOptions}
                            key={`users-dropdown-${usersOptions.length}`}
                            defaultValue={usersOptions.filter((el) => requestData.users.includes(el.value))}
                            onChange={(e) => handleOnChange(e, "msdd", "users")}
                        />
                    </Col>
                    <Col sm={12} md={2} className="d-flex justify-content-end">
                        <Button onClick={applyFilters} className="btn btn-primary w-100" disabled={loading}>
                            Αναζήτηση
                            {loading && <Spinner className="ml-2" animation="border" size="sm" variant="light" />}
                        </Button>
                    </Col>
                </Row>

                <hr />

                <Row className="mb-2 align-items-center">
                    <Col md={6} className="mb-2">
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleExportZip}
                            disabled={exportingZip || history.length === 0}
                        >
                            {exportingZip ? <Spinner animation="border" size="sm" className="mr-1" /> : <MdFolderZip className="mr-1" size={11} />}
                            {selectedRows.includes("-1")
                                ? (selectedRows.length > 1 ? `Εξαγωγή Όλων (Εκτός από ${selectedRows.length - 1})` : "Εξαγωγή Όλων")
                                : (selectedRows.length > 0 ? `Εξαγωγή επιλεγμένων (${selectedRows.length})` : "Εξαγωγή Όλων")
                            }
                        </Button>
                    </Col>
                    <Col md={6} className="d-flex justify-content-end">
                        {totalPages > 0 && (
                            <Pagination
                                currentPage={requestData.page}
                                totalPages={totalPages}
                                handlePageChange={onPageChange}
                                maxWidth={400}
                            />
                        )}
                    </Col>
                </Row>

                {selectedRows.length > 0 && (
                    <div className="d-flex align-items-center mb-2 p-2 bg-light border rounded">
                        <i>
                            Έχετε επιλέξει {selectedRows.includes("-1")
                            ? totalItems - (selectedRows.length - 1)
                            : selectedRows.length} συνομιλίες
                        </i>
                        <div className="ml-3 pr-2 text-danger d-flex align-items-center"
                             title="Αποεπιλογή"
                             onClick={() => setSelectedRows([])}
                             style={{cursor: "pointer", fontWeight: "bold"}}>
                            <MdDeselect size={20} className="mr-1" /> Αποεπιλογή όλων
                        </div>
                    </div>
                )}

                {loading && history.length === 0 ? (
                    <div className="text-center p-4">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2">Φόρτωση δεδομένων...</p>
                    </div>
                ) : (
                    <table className="table-hover" style={{ tableLayout: "fixed", textAlign: "center", width: "100%" }}>
                        <colgroup>
                            <col style={{ width: "40px" }} />
                            <col style={{ width: "50px" }} />
                            <col style={{ width: "160px" }} />
                            <col style={{ width: "160px" }} />
                            <col style={{ width: "100px" }} />
                            <col style={{ width: "180px" }} />
                            <col style={{ width: "100px" }} />
                        </colgroup>
                        <thead style={{ position: "sticky", top: 0, backgroundColor: "white" }}>
                        <tr>
                            <th style={{ border: "1px solid #dee2e6", backgroundColor: "#f1fdff", padding: "8px" }}>
                                <input
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    checked={isAllChecked}
                                    style={{ cursor: "pointer", width: "16px", height: "16px" }}
                                    title="Επιλογή Όλων"
                                />
                            </th>
                            <th style={{ border: "1px solid #dee2e6", backgroundColor: "#f1fdff", padding: "8px" }}>Αρ.</th>
                            <th style={{ border: "1px solid #dee2e6", backgroundColor: "#f1fdff", padding: "8px" }}>Ημερομηνία</th>
                            <th style={{ border: "1px solid #dee2e6", backgroundColor: "#f1fdff", padding: "8px" }}>Λεπτομέρειες</th>
                            <th style={{ border: "1px solid #dee2e6", backgroundColor: "#f1fdff", padding: "8px" }}>Mode</th>
                            <th style={{ border: "1px solid #dee2e6", backgroundColor: "#f1fdff", padding: "8px" }}>Χρήστης</th>
                            <th style={{ border: "1px solid #dee2e6", backgroundColor: "#f1fdff", padding: "8px" }}>Μηνύματα</th>
                        </tr>
                        </thead>
                        <tbody>
                        {history.length > 0 ? history.map((session, index) => {
                            const isSelected = selectedRows.includes("-1")
                                ? !selectedRows.includes(String(session._id))
                                : selectedRows.includes(String(session._id));

                            return (
                                <tr
                                    key={session._id}
                                    onClick={() => onSelectSession(session)}
                                    style={{
                                        cursor: "pointer",
                                        backgroundColor: isSelected ? "#fff9e6" : "inherit"
                                    }}
                                    title={"Δείτε την συνομιλία"}
                                >
                                    <td onClick={(e) => e.stopPropagation()} style={{ border: "1px solid #dee2e6", padding: "6px" }}>
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={(e) => handleSelectRow(e, session._id)}
                                            style={{ cursor: "pointer", width: "16px", height: "16px" }}
                                        />
                                    </td>
                                    <td style={{ border: "1px solid #dee2e6", padding: "6px" }}>
                                        {(requestData.page - 1) * 10 + index + 1}
                                    </td>
                                    <td style={{ border: "1px solid #dee2e6", padding: "6px" }}>
                                        {moment(session.updated_at).format('DD/MM/YYYY HH:mm')}
                                    </td>
                                    <td style={{ border: "1px solid #dee2e6", padding: "6px" }}>
                                        {session.clientIdentifier || "-"}
                                    </td>
                                    <td style={{ border: "1px solid #dee2e6", padding: "6px" }}>
                                        {session.mode === "summary" ? "Περίληψη" : "Transcript"}
                                    </td>
                                    <td style={{ border: "1px solid #dee2e6", padding: "6px" }}>
                                        {session.userName || session.user || "-"}
                                    </td>
                                    <td style={{ border: "1px solid #dee2e6", padding: "6px" }}>
                                        {session.history?.length || 0}
                                    </td>
                                </tr>
                            )
                        }) : (
                            <tr>
                                <td colSpan={7} className="py-4 text-muted" style={{ border: "1px solid #dee2e6", backgroundColor: "#fcfcfc" }}>
                                    {loading ? "Φόρτωση..." : "Δεν βρέθηκαν συνομιλίες."}
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                )}
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between align-items-center">
                <div className="text-muted" style={{ fontSize: "13px", fontStyle: "italic" }}>
                    {latestUpdateText ? `Τελευταία δραστηριότητα: ${latestUpdateText}` : ""}
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default AIChatHistoryModal;